document.documentElement.classList.remove('no-js');

(() => {
  const root = window.BlendRush?.routes?.root || '/';
  const moneyFormat = window.BlendRush?.moneyFormat || '${{amount}}';
  const overlay = () => document.querySelector('[data-drawer-overlay]');

  const formatMoney = (cents) => {
    const value = (Number(cents || 0) / 100).toFixed(2);
    return moneyFormat.replace(/\{\{\s*amount(_with_comma_separator)?\s*\}\}/, value);
  };

  const closeDrawers = () => {
    document.querySelectorAll('.drawer.is-open').forEach(drawer => {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
    });
    if (overlay()) overlay().hidden = true;
    document.body.classList.remove('drawer-open');
  };

  const openDrawer = (selector) => {
    closeDrawers();
    const drawer = document.querySelector(selector);
    if (!drawer) return;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    if (overlay()) overlay().hidden = false;
    document.body.classList.add('drawer-open');
    requestAnimationFrame(() => drawer.querySelector('button, input, a')?.focus());
  };

  const updateCartCount = async () => {
    try {
      const response = await fetch(`${window.BlendRush.routes.cart}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      document.querySelectorAll('[data-cart-count]').forEach(el => {
        el.textContent = cart.item_count;
        el.setAttribute('aria-label', `${cart.item_count} items`);
      });
    } catch (error) { console.error(error); }
  };

  const refreshCartDrawer = async (open = false) => {
    try {
      const response = await fetch(`${root}?section_id=cart-drawer`);
      if (!response.ok) throw new Error('Unable to refresh cart');
      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const replacement = parsed.querySelector('[data-cart-drawer]');
      const current = document.querySelector('[data-cart-drawer]');
      if (replacement && current) current.replaceWith(replacement);
      await updateCartCount();
      if (open) openDrawer('[data-cart-drawer]');
    } catch (error) { console.error(error); }
  };

  const changeCartLine = async (key, quantity) => {
    const response = await fetch(`${window.BlendRush.routes.cartChange}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity })
    });
    if (!response.ok) throw new Error('Unable to update cart');
    await refreshCartDrawer(true);
  };

  document.addEventListener('click', async event => {
    const menuToggle = event.target.closest('[data-mobile-menu-toggle]');
    if (menuToggle) {
      const menu = document.querySelector('[data-mobile-menu]');
      const isOpen = menuToggle.getAttribute('aria-expanded') === 'true';
      menuToggle.setAttribute('aria-expanded', String(!isOpen));
      if (menu) menu.hidden = isOpen;
      return;
    }

    if (event.target.closest('[data-open-search]')) { openDrawer('[data-search-drawer]'); return; }
    if (event.target.closest('[data-open-cart]')) { openDrawer('[data-cart-drawer]'); return; }
    if (event.target.closest('[data-close-drawer]') || event.target.matches('[data-drawer-overlay]')) { closeDrawers(); return; }

    const thumb = event.target.closest('[data-media-target]');
    if (thumb) {
      const gallery = thumb.closest('[data-product-gallery]');
      gallery?.querySelectorAll('.is-active').forEach(el => el.classList.remove('is-active'));
      thumb.classList.add('is-active');
      gallery?.querySelector(`[data-media-id="${thumb.dataset.mediaTarget}"]`)?.classList.add('is-active');
      return;
    }

    const qtyControl = event.target.closest('[data-quantity-control]');
    if (qtyControl && (event.target.closest('[data-quantity-minus]') || event.target.closest('[data-quantity-plus]'))) {
      const input = qtyControl.querySelector('input');
      if (!input) return;
      const delta = event.target.closest('[data-quantity-plus]') ? 1 : -1;
      input.value = Math.max(Number(input.min || 0), Number(input.value || 0) + delta);
      if (input.matches('[data-cart-quantity]')) {
        const line = input.closest('[data-cart-line]');
        if (line) await changeCartLine(line.dataset.lineKey, Number(input.value));
      }
      return;
    }

    const remove = event.target.closest('[data-remove-line]');
    if (remove) {
      const line = remove.closest('[data-cart-line]');
      if (line) await changeCartLine(line.dataset.lineKey, 0);
      return;
    }

    const stickyAdd = event.target.closest('[data-sticky-add]');
    if (stickyAdd) { document.querySelector('[data-product-form] [data-add-to-cart]')?.click(); return; }

    const wishlist = event.target.closest('[data-wishlist-button]');
    if (wishlist) {
      const handle = wishlist.dataset.productHandle;
      const saved = JSON.parse(localStorage.getItem('blendrush-wishlist') || '[]');
      const next = saved.includes(handle) ? saved.filter(item => item !== handle) : [...saved, handle];
      localStorage.setItem('blendrush-wishlist', JSON.stringify(next));
      wishlist.classList.toggle('is-saved', next.includes(handle));
    }
  });

  document.addEventListener('keydown', event => { if (event.key === 'Escape') closeDrawers(); });

  document.addEventListener('submit', async event => {
    const productForm = event.target.closest('[data-product-form]');
    if (productForm) {
      event.preventDefault();
      const button = productForm.querySelector('[type="submit"]');
      const message = productForm.querySelector('[data-product-form-message]');
      if (button) button.disabled = true;
      if (message) message.textContent = 'Adding to cart…';
      try {
        const response = await fetch(`${window.BlendRush.routes.cartAdd}.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(productForm) });
        if (!response.ok) { const data = await response.json(); throw new Error(data.description || 'Unable to add item'); }
        if (message) message.textContent = 'Added to your cart.';
        await refreshCartDrawer(true);
      } catch (error) {
        if (message) message.textContent = error.message;
      } finally { if (button) button.disabled = false; }
      return;
    }

    const bundleForm = event.target.closest('[data-bundle-form]');
    if (bundleForm) {
      event.preventDefault();
      const items = [...bundleForm.querySelectorAll('[data-bundle-item]:checked')].map(input => ({ id: Number(input.value), quantity: 1 }));
      const message = bundleForm.querySelector('[data-bundle-message]');
      if (!items.length) { if (message) message.textContent = 'Select at least one item.'; return; }
      try {
        const response = await fetch(`${window.BlendRush.routes.cartAdd}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items }) });
        if (!response.ok) throw new Error('Unable to add bundle');
        if (message) message.textContent = 'Bundle added to your cart.';
        await refreshCartDrawer(true);
      } catch (error) { if (message) message.textContent = error.message; }
    }
  });

  document.addEventListener('change', event => {
    const select = event.target.closest('[data-variant-select]');
    if (!select) return;
    const option = select.selectedOptions[0];
    const section = select.closest('[data-product-section]');
    const price = section?.querySelector('[data-product-price]');
    const stickyPrice = section?.querySelector('[data-sticky-price]');
    if (price) {
      const compare = Number(option.dataset.comparePrice || 0);
      const current = Number(option.dataset.price || 0);
      price.innerHTML = `${compare > current ? `<s>${formatMoney(compare)}</s>` : ''}<strong>${formatMoney(current)}</strong>`;
      if (stickyPrice) stickyPrice.textContent = formatMoney(current);
    }
    const mediaId = option.dataset.featuredMediaId;
    if (mediaId) section?.querySelector(`[data-media-target="${mediaId}"]`)?.click();
  });

  let searchTimer;
  document.addEventListener('input', event => {
    const input = event.target.closest('[data-predictive-search-input]');
    if (!input) return;
    clearTimeout(searchTimer);
    const results = document.querySelector('[data-predictive-results]');
    const query = input.value.trim();
    if (query.length < 2) { if (results) results.innerHTML = '<p class="predictive-results__empty">Keep typing to see results.</p>'; return; }
    searchTimer = setTimeout(async () => {
      try {
        const url = `${window.BlendRush.routes.predictiveSearch}.json?q=${encodeURIComponent(query)}&resources[type]=product,collection,page&resources[limit]=6`;
        const response = await fetch(url);
        const data = await response.json();
        const products = data.resources?.results?.products || [];
        const collections = data.resources?.results?.collections || [];
        const pages = data.resources?.results?.pages || [];
        if (!results) return;
        const productHtml = products.map(item => `<a class="predictive-item" href="${item.url}">${item.image ? `<img src="${item.image}" alt="">` : ''}<span><strong>${item.title}</strong><small>${item.price || ''}</small></span></a>`).join('');
        const otherHtml = [...collections, ...pages].map(item => `<a class="predictive-item predictive-item--text" href="${item.url}"><span><strong>${item.title}</strong></span></a>`).join('');
        results.innerHTML = productHtml + otherHtml || '<p class="predictive-results__empty">No results found.</p>';
      } catch (error) { if (results) results.innerHTML = '<p class="predictive-results__empty">Search is temporarily unavailable.</p>'; }
    }, 250);
  });

  document.querySelectorAll('[data-product-recommendations]').forEach(async section => {
    if (section.innerHTML.trim()) return;
    try {
      const response = await fetch(section.dataset.url);
      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const replacement = parsed.querySelector('[data-product-recommendations]');
      if (replacement?.innerHTML.trim()) section.innerHTML = replacement.innerHTML;
    } catch (error) { console.error(error); }
  });

  const savedWishlist = JSON.parse(localStorage.getItem('blendrush-wishlist') || '[]');
  document.querySelectorAll('[data-wishlist-button]').forEach(button => button.classList.toggle('is-saved', savedWishlist.includes(button.dataset.productHandle)));
})();


// Phase 7: remove no-js marker and improve keyboard handling
document.documentElement.classList.remove('no-js');
document.documentElement.classList.add('js');
document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') {
    document.querySelectorAll('[data-search-drawer],[data-cart-drawer],[data-mobile-menu]').forEach((el) => {
      if (!el.hasAttribute('hidden')) el.setAttribute('hidden','');
    });
    const overlay = document.querySelector('[data-drawer-overlay]');
    if (overlay) overlay.setAttribute('hidden','');
    document.body.classList.remove('drawer-open');
  }
});
